import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaEdit, FaList, FaPlus, FaSearch, FaTrash, FaUserCog, FaUsers } from 'react-icons/fa';
import AdminPageHeader from './AdminPageHeader';
import 'bootstrap/dist/css/bootstrap.min.css';
import Navigation from './Navigation';
import useAuth from '../../hooks/useAuth';

function Employees() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const navigate = useNavigate();
  const { verifyAndRefreshToken, error } = useAuth();

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('sessionToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}karyawanlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ limit: 100, start: 0 }),
      });

      const data = await response.json();
      if (data.error === "0" && Array.isArray(data.results)) {
        setEmployees(data.results);
        setFilteredEmployees(data.results);
      } else {
        setEmployees([]);
        setFilteredEmployees([]);
        Swal.fire('Error', 'Failed to fetch employee data', 'error');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setEmployees([]);
      setFilteredEmployees([]);
      Swal.fire('Error', 'Failed to fetch employee data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      const token = localStorage.getItem('sessionToken');
      if (!token) {
        navigate('/loginadmin');
        return;
      }

      // Attempt to refresh token if needed
      const isValid = await verifyAndRefreshToken(token);
      if (isValid) {
        await fetchEmployees();
      }
    };

    initializeData();
  }, [navigate, verifyAndRefreshToken, fetchEmployees]);

  const handleSearch = useCallback((e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);

    // Safety check
    if (!Array.isArray(employees)) return;

    setFilteredEmployees(
      employees.filter(employee =>
        (employee?.nama || '').toLowerCase().includes(value) ||
        (employee?.OutletName || '').toLowerCase().includes(value)
      )
    );
  }, [employees]);

  const handleDelete = useCallback(async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This action cannot be undone!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      const token = localStorage.getItem('sessionToken');
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}deletekaryawan`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ id }),
        });

        const data = await response.json();
        if (data.error === "0") {
          Swal.fire('Deleted!', 'Employee has been deleted.', 'success');
          setEmployees(prev => prev.filter(emp => emp.id !== id));
          setFilteredEmployees(prev => prev.filter(emp => emp.id !== id));
        } else {
          Swal.fire('Error!', 'Failed to delete employee.', 'error');
        }
      } catch (err) {
        console.error('Delete error:', err);
        Swal.fire('Error!', 'Failed to delete employee.', 'error');
      }
    }
  }, []);

  const columns = [
    { name: 'ID', selector: row => row.id, sortable: true },
    {
      name: 'Employee',
      sortable: true,
      grow: 1.6,
      cell: row => (
        <div className="table-identity">
          <span className="table-identity__avatar">
            {row.nama?.slice(0, 2).toUpperCase() || 'EM'}
          </span>
          <span className="table-identity__meta">
            <strong>{row.nama}</strong>
            <small>{row.OutletName || 'No outlet assigned'}</small>
          </span>
        </div>
      ),
    },
    {
      name: 'Address',
      grow: 1.4,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.alamat || '-'}</strong>
          <small>{row.no_telp || 'No phone number'}</small>
        </span>
      ),
    },
    {
      name: 'Created At',
      selector: row => row.created_at,
      format: row => new Date(row.created_at).toLocaleDateString()
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="table-action-group">
          <Button
            variant="outline-warning"
            size="sm"
            title="Edit employee"
            onClick={() => navigate(`/edit-employee/${row.id}`)}
          >
            <FaEdit /> <span className="d-none d-md-inline">Edit</span>
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            title="Delete employee"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash /> <span className="d-none d-md-inline">Delete</span>
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      <Navigation />
      <Container className="admin-page-shell mt-4">
        <AdminPageHeader
          icon={FaUsers}
          eyebrow="Team management"
          title="Employee Management"
          description="Kelola data karyawan, penempatan outlet, dan akses kerja harian dari satu halaman."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Karyawan List' },
          ]}
          actions={(
            <Button variant="primary" onClick={() => navigate('/add-employee')}>
              <FaPlus /> Add Employee
            </Button>
          )}
          meta={(
            <>
              <span className="soft-stat">
                <FaList /> {filteredEmployees.length} employees
              </span>
              <span className="soft-stat">
                <FaUserCog /> Team access ready
              </span>
            </>
          )}
        />

        <div className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">
              <FaList /> {filteredEmployees.length} employees
            </span>
            <div className="control-with-icon search-control">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by name or outlet"
                value={search}
                onChange={handleSearch}
              />
            </div>
          </div>
          <AdminDataTable
            columns={columns}
            data={filteredEmployees || []}
            pagination
            progressPending={loading}
            highlightOnHover
            pointerOnHover
            striped
            responsive
            emptyTitle="No employee data found"
            emptyDescription="Tambahkan karyawan baru agar akses operasional bisa langsung dipakai."
          />
        </div>
      </Container>
    </div>
  );
}

export default Employees;
